import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer
} from "react";
import type { User } from "shared/types/customer";
import type { ApiErrorResponse } from "shared/types/error";
import { useIntercom } from "react-use-intercom";
import { usePostHog } from "posthog-js/react";
import { fetcher } from "../../api";
import { config } from "../../config";
import { datadogRum } from "@datadog/browser-rum";

export enum AuthState {
  LOADING = "loading",
  AUTHED = "authed",
  ERROR = "error"
}

interface AuthedState {
  state: AuthState.AUTHED;
  user: User;
  updateAuth: () => Promise<void>;
  unreadIntercomCount: number;
}

interface UnauthedState {
  state: AuthState.ERROR | AuthState.LOADING;
  user?: undefined;
  updateAuth: () => Promise<void>;
  unreadIntercomCount: number;
}

const AuthContext = createContext<AuthedState | UnauthedState>({
  state: AuthState.ERROR,
  updateAuth: () => Promise.resolve(),
  unreadIntercomCount: 0
});

const reducer = (
  state: AuthedState | UnauthedState,
  action: {
    type: AuthState;
    payload?: User;
  }
) => {
  switch (action.type) {
    case AuthState.LOADING:
      return {
        state: AuthState.LOADING
      };

    case AuthState.AUTHED:
      return {
        state: AuthState.AUTHED,
        user: action.payload
      };

    case AuthState.ERROR:
      return {
        state: AuthState.ERROR
      };

    default:
      return state;
  }
};
export const AuthProvider: FC<{
  children?: ReactNode;
  unreadIntercomCount: number;
}> = ({ children, unreadIntercomCount }) => {
  const [auth, dispatch] = useReducer(reducer, {
    state: AuthState.LOADING
  });
  const posthog = usePostHog();
  const intercom = useIntercom();

  const updateAuth = useCallback(
    () =>
      fetcher("/screen/auth")
        .then(
          (
            res:
              | ApiErrorResponse
              | {
                  customer: User;
                }
          ) => {
            if ("error" in res) throw new Error(res.error.message);

            dispatch({
              type: AuthState.AUTHED,
              payload: res.customer
            });
            intercom.update({
              customAttributes: {
                source: "boomscreen_applicant"
              },
              email: res.customer.email,
              name: res.customer.first_name,
              hideDefaultLauncher: true
            });

            datadogRum.setUser({
              id: res.customer.id,
              name: `${res.customer.first_name} ${res.customer.last_name}`,
              first_name: res.customer.first_name,
              last_name: res.customer.last_name,
              email: res.customer.email,
              phone: res.customer.phone
            });

            const identified = posthog._isIdentified();
            const payload = {
              env: config.environment,
              phone: res.customer.phone,
              customer_email: res.customer.email
            };
            if (identified) {
              const dId = posthog.get_distinct_id();
              // Reset identification if the phone number is different
              if (dId !== res.customer.phone) {
                posthog.reset();
                posthog.identify(String(res.customer.phone), payload);
              }
              // update the user's email
              else posthog.setPersonProperties(payload);
            } else posthog.identify(String(res.customer.phone), payload);
          }
        )
        .catch(() => {
          posthog.reset();
          datadogRum.clearUser();
          intercom.update({
            customAttributes: {
              source: "boomscreen_applicant"
            },
            hideDefaultLauncher: true
          });
          dispatch({
            type: AuthState.ERROR
          });
        }),
    []
  );

  const refreshAuth = useCallback(() => {
    dispatch({
      type: AuthState.LOADING
    });
    intercom.update({
      customAttributes: {
        source: "boomscreen_applicant"
      },
      hideDefaultLauncher: true
    });
    updateAuth();
  }, []);

  useEffect(refreshAuth, []);

  return (
    <AuthContext.Provider
      value={{
        ...(auth as AuthedState | UnauthedState),
        updateAuth,
        unreadIntercomCount
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
